import React, { ForwardedRef, RefObject, useContext, useEffect } from 'react';
import { useObjectRef } from 'react-aria/useObjectRef';

import { TabProps } from '../TabsProps';
import { UnderlinedTabBase } from './UnderlinedTabBase';
import {
  TabElementType,
  TabsContext,
  TabsContextValue,
  useIntersectionObserver,
} from './UnderlinedTabsList';

export const UnderlinedNavTab = React.forwardRef(function UnderlinedNavTab(
  props: TabProps,
  ref: ForwardedRef<TabElementType>
): JSX.Element {
  const contextValue = useContext<TabsContextValue | null>(TabsContext);

  if (!contextValue) {
    throw new Error('Component must be wrapped with <TabsContext.Provider>');
  }

  const {
    registerTab,
    deregisterTab,
    setActiveTabRef,
    intersectionObserver,
    scrollTabIntoView,
  } = contextValue;
  const tabRef = useObjectRef(ref);

  useIntersectionObserver(
    tabRef as RefObject<TabElementType>,
    intersectionObserver
  );

  useEffect(() => {
    if (tabRef.current && props.isActive) {
      const tab = tabRef.current;
      setTimeout(() => {
        scrollTabIntoView(tab, false);
      }, 0);
    }
  }, [tabRef.current]);

  useEffect(() => {
    if (tabRef.current && props.isActive) {
      scrollTabIntoView(tabRef.current);
      setActiveTabRef(tabRef);
    }
  }, [props.isActive, tabRef.current]);

  useEffect(() => {
    if (tabRef.current && registerTab) {
      registerTab(tabRef);
    }

    return () => deregisterTab(tabRef);
  }, [tabRef]);

  return (
    <UnderlinedTabBase ref={tabRef} {...props}>
      {props.children}
    </UnderlinedTabBase>
  );
});
